import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DoctorCharacter from './DoctorCharacter';
import RiderCharacter from './RiderCharacter';
import BakerCharacter from './BakerCharacter';
import TrainDriverCharacter from './TrainDriverCharacter';
import BonzoCharacter from './BonzoCharacter';
import WatchmakerCharacter from './WatchmakerCharacter';
import { getLevelById, ARCADE_LEVELS } from './arcadeData';

const CHARACTER_COMPONENTS = {
  doctor: DoctorCharacter,
  rider: RiderCharacter,
  baker: BakerCharacter,
  traindriver: TrainDriverCharacter,
  bonzo: BonzoCharacter,
  watchmaker: WatchmakerCharacter,
};

const ArcadeIntro = () => {
  const { levelId = 'doctor' } = useParams();
  const level = getLevelById(levelId) || ARCADE_LEVELS[0];
  const navigate = useNavigate();

  const [displayedText, setDisplayedText] = useState('');
  const [typingDone, setTypingDone] = useState(false);
  const [entering, setEntering] = useState(false);
  const [characterBob, setCharacterBob] = useState(false);
  const typingRef = useRef(null);

  // Typewriter effect
  useEffect(() => {
    setDisplayedText('');
    setTypingDone(false);
    let i = 0;
    const msg = level.introMessage;
    typingRef.current = setInterval(() => {
      i++;
      setDisplayedText(msg.slice(0, i));
      if (i >= msg.length) {
        clearInterval(typingRef.current);
        setTypingDone(true);
      }
    }, 28);
    return () => clearInterval(typingRef.current);
  }, [level.introMessage]);

  // Bob animation after typing done
  useEffect(() => {
    if (!typingDone) return;
    const t = setTimeout(() => setCharacterBob(true), 200);
    return () => clearTimeout(t);
  }, [typingDone]);

  const handleSkipTyping = () => {
    if (!typingDone) {
      clearInterval(typingRef.current);
      setDisplayedText(level.introMessage);
      setTypingDone(true);
    }
  };

  const handleEnter = () => {
    setEntering(true);
    setTimeout(() => {
      navigate(`/arcade/game/${level.id}`);
    }, 700);
  };

  return (
    <div style={styles.root}>
      {/* Scanline overlay for game feel */}
      <div style={styles.scanlines} />

      {/* Top HUD bar */}
      <div style={styles.hud}>
        <button style={styles.backButton} onClick={() => navigate(localStorage.getItem('arcadeReturn') || '/')}>
          ← BACK
        </button>
        <div style={styles.levelBadge}>
          <span style={styles.levelNum}>LEVEL {level.levelNumber}</span>
        </div>
        <div style={{ ...styles.difficultyTag, background: level.difficultyColor }}>
          {level.difficulty}
        </div>
      </div>

      {/* VS-screen layout */}
      <div style={styles.arena}>
        {/* Left: character + name plate */}
        <div style={{ ...styles.characterSide, ...(entering ? styles.characterEntering : {}) }}>
          {/* Character name banner */}
          <div style={styles.nameBanner}>
            <div style={styles.nameTitle}>{level.title}</div>
            <div style={styles.characterName}>{level.characterName}</div>
          </div>

          {/* Character sprite with glow */}
          <div style={{ ...styles.characterStage, ...(characterBob ? styles.bobAnim : {}) }}>
            <div style={styles.characterGlow} />
            {React.createElement(CHARACTER_COMPONENTS[levelId] || DoctorCharacter, { width: level.characterWidth || 220, height: level.characterHeight || 310 })}
            {/* Ground platform */}
            <div style={styles.platform} />
          </div>

          {/* Stats plate */}
          <div style={styles.statsPlate}>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>TARGET MAE</span>
              <span style={styles.statValue}>≤ {level.targetMAE} ms</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>CLICKS</span>
              <span style={styles.statValue}>{level.timeHorizon}</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>CHALLENGE</span>
              <span style={{ ...styles.statValue, color: '#ffd740', fontSize: '0.75rem' }}>
                Beat {level.characterName}
              </span>
            </div>
          </div>
        </div>

        {/* Right: speech bubble + action */}
        <div style={styles.dialogSide}>
          {/* Speech bubble */}
          <div style={styles.speechContainer} onClick={handleSkipTyping}>
            <div style={styles.speechBubble}>
              <div style={styles.speechTail} />
              <p style={styles.speechText}>
                {displayedText}
                {!typingDone && <span style={styles.cursor}>▎</span>}
              </p>
            </div>
            {!typingDone && (
              <div style={styles.skipHint}>tap to skip</div>
            )}
          </div>

          {/* Challenge card */}
          {typingDone && (
            <div style={styles.challengeCard}>
              <div style={styles.challengeHeader}>⚔ CHALLENGE</div>
              <div style={styles.challengeText}>
                Achieve a MAE of <strong style={{ color: '#ffd740' }}>{level.targetMAE}ms or less</strong><br />
                in {level.timeHorizon} clicks
              </div>
              <div style={styles.challengeMeter}>
                <div style={styles.meterLabel}>DIFFICULTY</div>
                <div style={styles.meterBar}>
                  <div style={{ ...styles.meterFill, width: '20%', background: level.difficultyColor }} />
                </div>
                <div style={{ ...styles.meterDifficulty, color: level.difficultyColor }}>
                  {level.difficulty}
                </div>
              </div>
            </div>
          )}

          {/* Enter button */}
          {typingDone && (
            <button
              style={{ ...styles.enterButton, ...(entering ? styles.enterButtonActive : {}) }}
              onClick={handleEnter}
            >
              <span style={styles.enterIcon}>{level.enterEmoji || '🏥'}</span>
              {level.enterLabel || 'ENTER THE OFFICE'}
              <span style={styles.enterArrow}>▶</span>
            </button>
          )}
        </div>
      </div>

      {/* Bottom: particle dots decoration */}
      <div style={styles.particles}>
        {[...Array(8)].map((_, i) => (
          <div key={i} style={{ ...styles.particle, animationDelay: `${i * 0.4}s`, left: `${10 + i * 11}%` }} />
        ))}
      </div>

      <style>{`
          @keyframes bob {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(-40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideOut {
          to { opacity: 0; transform: translateX(-120px) scale(0.92); }
        }
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.08); }
        }
        @keyframes particleFly {
          0% { transform: translateY(0) scale(1); opacity: 0.7; }
          100% { transform: translateY(-80px) scale(0.4); opacity: 0; }
        }
        @keyframes enterPop {
          0% { transform: scale(1); }
          50% { transform: scale(0.94); }
          100% { transform: scale(1); }
        }
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  root: {
    minHeight: '100vh',
    background: 'radial-gradient(ellipse at 20% 50%, #0d1b2a 0%, #060e18 60%, #0a0a0a 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontFamily: "'Press Start 2P', 'Courier New', monospace",
    position: 'relative',
    overflow: 'hidden',
  },
  scanlines: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.06) 2px, rgba(0,0,0,0.06) 4px)',
    pointerEvents: 'none',
    zIndex: 10,
  },
  hud: {
    width: '100%',
    maxWidth: '960px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderBottom: '2px solid rgba(255,215,64,0.3)',
    boxSizing: 'border-box',
  },
  backButton: {
    background: 'none',
    border: '2px solid rgba(255,255,255,0.3)',
    color: 'rgba(255,255,255,0.6)',
    fontFamily: "'Press Start 2P', monospace",
    fontSize: '0.6rem',
    padding: '8px 14px',
    cursor: 'pointer',
    letterSpacing: '0.05em',
    transition: 'all 0.2s',
  },
  levelBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  levelNum: {
    color: '#ffd740',
    fontSize: '0.9rem',
    letterSpacing: '0.12em',
    textShadow: '0 0 20px rgba(255,215,64,0.6)',
  },
  difficultyTag: {
    color: 'white',
    fontSize: '0.55rem',
    padding: '6px 12px',
    letterSpacing: '0.1em',
    fontFamily: "'Press Start 2P', monospace",
    boxShadow: '0 0 12px rgba(0,0,0,0.4)',
  },
  arena: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '32px',
    padding: '24px 24px 16px',
    width: '100%',
    maxWidth: '960px',
    flex: 1,
    flexWrap: 'wrap',
  },
  characterSide: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    animation: 'fadeSlideIn 0.5s ease-out forwards',
    minWidth: '260px',
  },
  characterEntering: {
    animation: 'slideOut 0.7s ease-in forwards',
  },
  nameBanner: {
    background: 'linear-gradient(135deg, #1a2a4a 0%, #0f1a30 100%)',
    border: '2px solid #ffd740',
    padding: '10px 24px',
    textAlign: 'center',
    boxShadow: '0 0 20px rgba(255,215,64,0.3)',
    width: '100%',
    boxSizing: 'border-box',
  },
  nameTitle: {
    color: 'rgba(255,215,64,0.7)',
    fontSize: '0.5rem',
    letterSpacing: '0.2em',
    marginBottom: '4px',
  },
  characterName: {
    color: '#ffffff',
    fontSize: '0.9rem',
    letterSpacing: '0.1em',
    textShadow: '0 0 16px rgba(255,255,255,0.5)',
  },
  characterStage: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    animation: 'bob 2.8s ease-in-out infinite',
  },
  bobAnim: {
    animation: 'bob 2.8s ease-in-out infinite',
  },
  characterGlow: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '180px',
    height: '80px',
    background: 'radial-gradient(ellipse, rgba(100,180,255,0.25) 0%, transparent 70%)',
    animation: 'glowPulse 2.8s ease-in-out infinite',
    pointerEvents: 'none',
    borderRadius: '50%',
  },
  platform: {
    width: '180px',
    height: '14px',
    background: 'linear-gradient(180deg, rgba(100,160,255,0.35), rgba(60,100,200,0.1))',
    borderRadius: '50%',
    marginTop: '-8px',
    boxShadow: '0 0 20px rgba(100,160,255,0.4)',
  },
  statsPlate: {
    background: 'rgba(10,20,40,0.9)',
    border: '2px solid rgba(100,160,255,0.4)',
    padding: '12px 18px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    paddingBottom: '6px',
  },
  statLabel: {
    color: 'rgba(180,200,255,0.7)',
    fontSize: '0.5rem',
    letterSpacing: '0.08em',
  },
  statValue: {
    color: '#ffffff',
    fontSize: '0.6rem',
    fontWeight: 'bold',
  },
  dialogSide: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    flex: 1,
    minWidth: '280px',
    maxWidth: '420px',
    paddingTop: '10px',
  },
  speechContainer: {
    position: 'relative',
    cursor: 'pointer',
  },
  speechBubble: {
    background: 'rgba(250,248,240,0.96)',
    border: '3px solid #2e1f14',
    borderRadius: '16px',
    padding: '20px 22px',
    position: 'relative',
    boxShadow: '4px 4px 0 #2e1f14, 0 0 30px rgba(255,230,150,0.15)',
  },
  speechTail: {
    position: 'absolute',
    left: '-22px',
    top: '30px',
    width: 0,
    height: 0,
    borderTop: '10px solid transparent',
    borderBottom: '10px solid transparent',
    borderRight: '22px solid #2e1f14',
  },
  speechText: {
    fontFamily: "'Press Start 2P', monospace",
    fontSize: '0.62rem',
    lineHeight: '1.9',
    color: '#2a1a0e',
    margin: 0,
    minHeight: '80px',
  },
  cursor: {
    animation: 'cursorBlink 0.8s step-end infinite',
    color: '#555',
  },
  skipHint: {
    color: 'rgba(180,180,180,0.5)',
    fontSize: '0.45rem',
    textAlign: 'right',
    marginTop: '4px',
    fontFamily: 'monospace',
    letterSpacing: '0.05em',
  },
  challengeCard: {
    background: 'linear-gradient(135deg, #1a2035 0%, #0e1525 100%)',
    border: '2px solid rgba(255,215,64,0.5)',
    padding: '16px 20px',
    boxShadow: '0 0 20px rgba(255,215,64,0.15)',
    animation: 'fadeSlideIn 0.4s ease-out',
  },
  challengeHeader: {
    color: '#ffd740',
    fontSize: '0.65rem',
    letterSpacing: '0.15em',
    marginBottom: '10px',
    textShadow: '0 0 10px rgba(255,215,64,0.5)',
  },
  challengeText: {
    color: 'rgba(220,230,255,0.85)',
    fontSize: '0.55rem',
    lineHeight: '2',
    marginBottom: '14px',
  },
  challengeMeter: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  meterLabel: {
    color: 'rgba(180,200,255,0.6)',
    fontSize: '0.45rem',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
  },
  meterBar: {
    flex: 1,
    height: '8px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    overflow: 'hidden',
  },
  meterFill: {
    height: '100%',
    transition: 'width 1s ease',
    boxShadow: '0 0 8px currentColor',
  },
  meterDifficulty: {
    fontSize: '0.5rem',
    letterSpacing: '0.05em',
    minWidth: '30px',
  },
  enterButton: {
    width: '100%',
    padding: '16px 24px',
    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
    border: '3px solid #42a5f5',
    color: 'white',
    fontFamily: "'Press Start 2P', monospace",
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 6px 0 #0a2d6e, 0 0 30px rgba(66,165,245,0.4)',
    transition: 'all 0.15s',
    animation: 'fadeSlideIn 0.4s ease-out 0.1s both',
    textShadow: '0 1px 3px rgba(0,0,0,0.5)',
  },
  enterButtonActive: {
    transform: 'translateY(4px)',
    boxShadow: '0 2px 0 #0a2d6e, 0 0 20px rgba(66,165,245,0.3)',
    animation: 'enterPop 0.15s ease',
  },
  enterIcon: {
    fontSize: '1.1rem',
  },
  enterArrow: {
    color: '#90caf9',
    fontSize: '0.9rem',
  },
  particles: {
    position: 'fixed',
    bottom: '20px',
    left: 0,
    right: 0,
    height: '80px',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'flex-end',
  },
  particle: {
    position: 'absolute',
    width: '4px',
    height: '4px',
    background: 'rgba(100,180,255,0.6)',
    borderRadius: '50%',
    animation: 'particleFly 2.5s ease-in infinite',
    bottom: 0,
  },
};

export default ArcadeIntro;
